import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';

// components
import Root from './Root';
import PrivateRoute from './PrivateRoute';
import AdminRoute from './AdminRoute';
import SuperAdminRoute from './SuperAdminRoute';
import PermissionGuard from './PermissionGuard';
import { PermissionActions } from '../hooks/usePermissions';

// admin components
const AdminLogin = React.lazy(() => import('../features/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('../features/admin/AdminDashboard'));
const AdminProfile = React.lazy(() => import('../features/admin/AdminProfile'));
const UsersPage = React.lazy(() => import('../features/admin/UsersPage'));
const UserForm = React.lazy(() => import('../features/admin/UserForm'));
const AccessControlPage = React.lazy(() => import('../features/admin/AccessControlPage'));
const RolesPage = React.lazy(() => import('../features/admin/RolesPage'));
const PermissionsPage = React.lazy(() => import('../features/admin/PermissionsPage'));
const PermissionCreatePage = React.lazy(() => import('../features/admin/PermissionCreatePage'));
const PermissionEditPage = React.lazy(() => import('../features/admin/PermissionEditPage'));
const MySalesPage = React.lazy(() => import('../features/admin/MySalesPage'));
const MySaleDetailPage = React.lazy(() => import('../features/admin/MySaleDetailPage'));

// super admin components
const SuperAdminDashboard = React.lazy(() => import('../features/super-admin/SuperAdminDashboard'));
const ClientsListPage = React.lazy(() => import('../features/super-admin/ClientsListPage'));
const ClientForm = React.lazy(() => import('../features/super-admin/ClientForm'));
const ClientDetailPage = React.lazy(() => import('../features/super-admin/ClientDetailPage'));
const ContactQueriesPage = React.lazy(() => import('../features/super-admin/ContactQueriesPage'));

// supplier components
const SuppliersPage = React.lazy(() => import('../features/suppliers/SuppliersPage'));
const SupplierForm = React.lazy(() => import('../features/suppliers/SupplierForm'));
const SupplierDetail = React.lazy(() => import('../features/suppliers/SupplierDetail'));

// user
const UserProfile = React.lazy(() => import('../features/user/userProfile'));
const ItemList = React.lazy(() => import('../features/stock/stocks'));
const AddNewItem = React.lazy(() => import('../features/stock/addNewItems/addNewItem'));
const ProductEdit = React.lazy(() => import('../features/stock/productEdit/ProductEdit'));
const Category = React.lazy(() => import('../features/category'));
const Store = React.lazy(() => import('../features/store'));
const Transfer = React.lazy(() => import('../features/transfer'));

// demand (restaurant only)
const DemandListPage = React.lazy(() => import('../features/demand/DemandListPage'));
const DemandCreatePage = React.lazy(() => import('../features/demand/DemandCreatePage'));
const DemandDetailPage = React.lazy(() => import('../features/demand/DemandDetailPage'));
const DemandInvoicesPage = React.lazy(() => import('../features/demand/DemandInvoicesPage'));

// menu (restaurant only)
const MenuListPage = React.lazy(() => import('../features/menu/MenuListPage'));
const MenuItemForm = React.lazy(() => import('../features/menu/MenuItemForm'));
const MenuCategoryManagement = React.lazy(() => import('../features/menu/MenuCategoryManagement'));

// notifications
const NotificationsPage = React.lazy(() => import('../features/notifications'));

// stock logs
const StockLogsPage = React.lazy(() => import('../features/stock-logs/StockLogsPage'));

// barcode
const BarcodeList = React.lazy(() => import('../features/barcode/BarcodeList'));

// public pages
const PublicCustomerEInvoice = React.lazy(() => import('../pages/PublicCustomerEInvoice'));
const PublicCustomerEInvoiceComplete = React.lazy(() => import('../pages/PublicCustomerEInvoiceComplete'));
const TestDesign = React.lazy(() => import('../pages/TestDesign'));

// billing
const BillingPage = React.lazy(() => import('../features/billing/BillingPage'));

// billing v2
const BillingV2Page = React.lazy(() => import('../features/billing-v2/BillingV2Page'));

// menu billing (restaurant POS with menu items)
const MenuBillingPage = React.lazy(() => import('../features/menu-billing/MenuBillingPage'));
const CustomersPage = React.lazy(() => import('../features/billing/CustomersPage'));
const SelectCustomerPage = React.lazy(() => import('../features/billing/SelectCustomerPage'));
const PaymentPage = React.lazy(() => import('../features/billing/PaymentPage'));
const CustomerInvoicePage = React.lazy(() => import('../features/billing/CustomerInvoicePage'));

// credit management
const CreditManagementPage = React.lazy(() => import('../features/credit/CreditManagementPage'));

// expenses
const ExpensesPage = React.lazy(() => import('../features/expenses/ExpensesPage'));
const PurchasesPage = React.lazy(() => import('../features/purchases/PurchasesPage'));
const OrdersPage = React.lazy(() => import('../features/orders/OrdersPage'));
const SupplierSalesReportPage = React.lazy(() => import('../features/reports/SupplierSalesReportPage'));
const ReportsPage = React.lazy(() => import('../features/reports/ReportsPage'));
// lazy load all the views
// landing page
const LandingPage = React.lazy(() => import('../features/landing/index'));
const QuotationsPage = React.lazy(() => import('../features/quotations/QuotationsPage'));

// auth
const Login = React.lazy(() => import('../features/login/index'))
const SignUp = React.lazy(() => import('../features/signup/index'));
const OTPForgotPassword = React.lazy(() => import('../features/forgot-password/OTPForgotPassword'));    
const ForgotPassword = React.lazy(() => import('../features/forgot-password/index'));
const ResetPassword = React.lazy(() => import('../features/forgot-password/ResetPassword'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const Confirm = React.lazy(() => import('../pages/auth/Confirm'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));

// home
const Home = React.lazy(() => import('../pages/Home'));

// landings
const App = React.lazy(() => import('../pages/landings/App'));
const Saas = React.lazy(() => import('../pages/landings/Saas/Saas'));
const Saas2 = React.lazy(() => import('../pages/landings/Saas/Saas2'));
const Startup = React.lazy(() => import('../pages/landings/Startup'));
const Software = React.lazy(() => import('../pages/landings/Software'));

const Agency = React.lazy(() => import('../pages/landings/Agency'));
const Coworking = React.lazy(() => import('../pages/landings/Coworking'));
const Crypto = React.lazy(() => import('../pages/landings/Crypto'));
const Marketing = React.lazy(() => import('../pages/landings/Marketing'));
const Portfolio = React.lazy(() => import('../pages/landings/Portfolio'));

// pages
const Dashboard = React.lazy(() => import('../features/dashboard/index'));
const Settings = React.lazy(() => import('../pages/other/account/Settings'));

const Blog = React.lazy(() => import('../pages/other/Blog'));
const BlogPost = React.lazy(() => import('../pages/other/BlogPost'));

const Company = React.lazy(() => import('../pages/other/Company'));
const Contact = React.lazy(() => import('../pages/other/Contact'));
const Career = React.lazy(() => import('../pages/other/Career'));
const Pricing = React.lazy(() => import('../pages/other/Pricing'));

const PortfolioGrid = React.lazy(() => import('../pages/other/portfolio/PortfolioGrid'));
const PortfolioMasonry = React.lazy(() => import('../pages/other/portfolio/PortfolioMasonry'));
const PortfolioItem = React.lazy(() => import('../pages/other/portfolio/PortfolioItem'));

const HelpDesk = React.lazy(() => import('../pages/other/HelpDesk'));

// docs
const Introduction = React.lazy(() => import('../pages/docs/Introduction'));
const QuickStart = React.lazy(() => import('../pages/docs/QuickStart'));
const Customization = React.lazy(() => import('../pages/docs/Customization'));
const Routing = React.lazy(() => import('../pages/docs/Routing'));
const CodeSpliting = React.lazy(() => import('../pages/docs/CodeSpliting'));
const ChangeLog = React.lazy(() => import('../pages/docs/ChangeLog'));

const Colors = React.lazy(() => import('../pages/docs/Colors'));
const Typography = React.lazy(() => import('../pages/docs/Typography'));

const Bootstrap = React.lazy(() => import('../pages/docs/Bootstrap'));
const Custom = React.lazy(() => import('../pages/docs/Custom'));
const Plugins = React.lazy(() => import('../pages/docs/Plugins'));

const Navbars = React.lazy(() => import('../pages/docs/Navbars'));
const Heros = React.lazy(() => import('../pages/docs/Heros'));

// 404 Not Found page
const NotFound = React.lazy(() => import('../pages/NotFound'));

const loading = () => <div className=""></div>;

type LoadComponentProps = {
    component: React.LazyExoticComponent<React.ComponentType<any>>;
};

const LoadComponent = ({ component: Component }: LoadComponentProps) => (
    <ErrorBoundary>
        <Suspense fallback={loading()}>
            <Component />
        </Suspense>
    </ErrorBoundary>
);

const AllRoutes = () => {
    return useRoutes([
        {
            // root route - landing page (must be first specific match)
            index: true,
            element: <LoadComponent component={LandingPage} />,
        },
        { path: 'home', element: <LoadComponent component={Home} /> },
        { path: 'login', element: <LoadComponent component={Login} /> },
        { path: 'forget-password', element: <LoadComponent component={OTPForgotPassword} /> },
        { 
            path: 'dashboard', 
            element: <PrivateRoute excludeRoles={['superadmin']} />,
            children: [
                { index: true, element: <LoadComponent component={Dashboard} /> }
            ]
        },
        {
            // User section routes - block superadmin
            path: '/',
            element: <PrivateRoute excludeRoles={['superadmin']} />,
            children: [
                { path: 'billing', element: <LoadComponent component={BillingPage} /> },
                { path: 'billing-v2', element: <LoadComponent component={BillingV2Page} /> },
                { path: 'my-sales', element: <LoadComponent component={MySalesPage} /> },
                { path: 'my-sales/:uuid', element: <LoadComponent component={MySaleDetailPage} /> },
                { path: 'customers', element: <LoadComponent component={CustomersPage} /> },
                { path: 'billing/select-customer', element: <LoadComponent component={SelectCustomerPage} /> },
                { path: 'billing/payment', element: <LoadComponent component={PaymentPage} /> },
                { path: 'billing/customer-invoice', element: <LoadComponent component={CustomerInvoicePage} /> },
                { path: 'credit', element: <LoadComponent component={CreditManagementPage} /> },
                { path: 'quotations', element: <LoadComponent component={QuotationsPage} /> },
                { path: 'expenses', element: <LoadComponent component={ExpensesPage} /> },
                { path: 'orders', element: <LoadComponent component={OrdersPage} /> },
                { path: 'reports', element: <LoadComponent component={ReportsPage} /> },
                { path: 'reports/supplier-profit-loss', element: <LoadComponent component={SupplierSalesReportPage} /> },
                {
                    path: 'purchases',
                    element: (
                        <PermissionGuard permission={PermissionActions.MANAGE_PURCHASES}>
                            <LoadComponent component={PurchasesPage} />
                        </PermissionGuard>
                    ),
                },
                { path: 'user-profile', element: <LoadComponent component={UserProfile} /> },
                { path: 'stock-list', element: <LoadComponent component={ItemList} /> },
                { path: 'stock-logs', element: <LoadComponent component={StockLogsPage} /> },
                { path: 'add-new-item', element: <LoadComponent component={AddNewItem} /> },
                { path: 'product/:id', element: <LoadComponent component={ProductEdit} /> },
                { path: 'categories', element: <LoadComponent component={Category} /> },
                { path: 'transfers', element: <LoadComponent component={Transfer} /> },
                { path: 'notifications', element: <LoadComponent component={NotificationsPage} /> },
                { path: 'barcodes', element: <LoadComponent component={BarcodeList} /> },
                { path: 'suppliers', element: <LoadComponent component={SuppliersPage} /> },
                { path: 'suppliers/new', element: <LoadComponent component={SupplierForm} /> },
                { path: 'suppliers/:uuid', element: <LoadComponent component={SupplierDetail} /> },
                { path: 'suppliers/:uuid/edit', element: <LoadComponent component={SupplierForm} /> },
                { path: 'stores', element: <LoadComponent component={Store} /> },
                // Menu Billing - Restaurant POS with menu items
                { path: 'menu-billing', element: <LoadComponent component={MenuBillingPage} /> },
                // Menu routes (restaurant only)
                { path: 'menu', element: <LoadComponent component={MenuListPage} /> },
                { path: 'menu/new', element: <LoadComponent component={MenuItemForm} /> },
                { path: 'menu/edit/:uuid', element: <LoadComponent component={MenuItemForm} /> },
                { path: 'menu/categories', element: <LoadComponent component={MenuCategoryManagement} /> },
                // Demand routes (restaurant only)
                { path: 'demands', element: <LoadComponent component={DemandListPage} /> },
                { path: 'demands/create', element: <LoadComponent component={DemandCreatePage} /> },
                { path: 'demands/invoices', element: <LoadComponent component={DemandInvoicesPage} /> },
                { path: 'demands/:uuid', element: <LoadComponent component={DemandDetailPage} /> },
            ]
        },
        {
            // public routes
            path: '/',
            children: [
                {
                    path: 'customer-einvoice/:billId',
                    element: <LoadComponent component={PublicCustomerEInvoice} />
                },
                {
                    path: 'customer-einvoice/:billId/complete',
                    element: <LoadComponent component={PublicCustomerEInvoiceComplete} />
                },
                {
                    path: 'testDesign',
                    element: <LoadComponent component={TestDesign} />
                },
                {
                    path: 'auth',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'forgot-password', element: <LoadComponent component={ForgotPassword} /> },
                        { path: 'reset-password', element: <LoadComponent component={ResetPassword} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                    ],
                },
                {
                    path: 'docs',
                    children: [
                        { path: 'introduction', element: <LoadComponent component={Introduction} /> },
                        { path: 'quick-start', element: <LoadComponent component={QuickStart} /> },
                        { path: 'customization', element: <LoadComponent component={Customization} /> },
                        { path: 'routing', element: <LoadComponent component={Routing} /> },
                        { path: 'code-spliting', element: <LoadComponent component={CodeSpliting} /> },
                        { path: 'change-log', element: <LoadComponent component={ChangeLog} /> },

                        { path: 'colors', element: <LoadComponent component={Colors} /> },
                        { path: 'typography', element: <LoadComponent component={Typography} /> },
                        { path: 'bootstrap', element: <LoadComponent component={Bootstrap} /> },
                        { path: 'custom', element: <LoadComponent component={Custom} /> },
                        { path: 'plugins', element: <LoadComponent component={Plugins} /> },
                        { path: 'navbars', element: <LoadComponent component={Navbars} /> },
                        { path: 'heros', element: <LoadComponent component={Heros} /> },
                    ],
                },
                {
                    path: 'landing',
                    children: [
                        { path: 'app', element: <LoadComponent component={App} /> },
                        { path: 'saas', element: <LoadComponent component={Saas} /> },
                        { path: 'saas2', element: <LoadComponent component={Saas2} /> },
                        { path: 'startup', element: <LoadComponent component={Startup} /> },
                        { path: 'software', element: <LoadComponent component={Software} /> },

                        { path: 'agency', element: <LoadComponent component={Agency} /> },
                        { path: 'coworking', element: <LoadComponent component={Coworking} /> },
                        { path: 'crypto', element: <LoadComponent component={Crypto} /> },
                        { path: 'marketing', element: <LoadComponent component={Marketing} /> },
                        { path: 'portfolio', element: <LoadComponent component={Portfolio} /> },
                    ],
                },
                {
                    path: 'pages',
                    children: [
                        {
                            path: 'blog',
                            children: [
                                { path: '', element: <LoadComponent component={Blog} /> },
                                { path: 'post', element: <LoadComponent component={BlogPost} /> },
                            ],
                        },
                        { path: 'company', element: <LoadComponent component={Company} /> },
                        { path: 'contact', element: <LoadComponent component={Contact} /> },
                        { path: 'career', element: <LoadComponent component={Career} /> },
                        { path: 'pricing', element: <LoadComponent component={Pricing} /> },
                        {
                            path: 'portfolio',
                            children: [
                                { path: 'grid', element: <LoadComponent component={PortfolioGrid} /> },
                                { path: 'masonry', element: <LoadComponent component={PortfolioMasonry} /> },
                                { path: 'item', element: <LoadComponent component={PortfolioItem} /> },
                            ],
                        },
                        { path: 'help', element: <LoadComponent component={HelpDesk} /> },
                    ]
                },
            ],
        },
        {
            // admin routes (public login)
            path: '/admin',
            children: [
                { 
                    path: 'login', 
                    element: (
                        <Suspense fallback={loading()}>
                            <AdminLogin />
                        </Suspense>
                    ) 
                },
            ],
        },
        {
            // admin protected routes
            path: '/admin',
            element: <AdminRoute />,
            children: [
                { 
                    path: 'dashboard', 
                    element: (
                        <Suspense fallback={loading()}>
                            <AdminDashboard />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'users', 
                    element: (
                        <Suspense fallback={loading()}>
                            <UsersPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'users/new', 
                    element: (
                        <Suspense fallback={loading()}>
                            <UserForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'users/:uuid/edit', 
                    element: (
                        <Suspense fallback={loading()}>
                            <UserForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'access-control', 
                    element: (
                        <Suspense fallback={loading()}>
                            <AccessControlPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'roles', 
                    element: (
                        <Suspense fallback={loading()}>
                            <RolesPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'permissions', 
                    element: (
                        <Suspense fallback={loading()}>
                            <PermissionsPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'customers', 
                    element: (
                        <Suspense fallback={loading()}>
                            <CustomersPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'credit', 
                    element: (
                        <Suspense fallback={loading()}>
                            <CreditManagementPage />
                        </Suspense>
                    ) 
                },
                {
                    path: 'quotations',
                    element: <LoadComponent component={QuotationsPage} />,
                },
                { 
                    path: 'expenses', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ExpensesPage />
                        </Suspense>
                    ) 
                },
                {
                    path: 'orders',
                    element: (
                        <Suspense fallback={loading()}>
                            <OrdersPage />
                        </Suspense>
                    )
                },
                { 
                    path: 'stock-list', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ItemList />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'stock-logs', 
                    element: (
                        <Suspense fallback={loading()}>
                            <StockLogsPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'add-new-item', 
                    element: (
                        <Suspense fallback={loading()}>
                            <AddNewItem />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'product/:id', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ProductEdit />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'categories', 
                    element: (
                        <Suspense fallback={loading()}>
                            <Category />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'stores', 
                    element: (
                        <Suspense fallback={loading()}>
                            <Store />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'transfers', 
                    element: (
                        <Suspense fallback={loading()}>
                            <Transfer />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'notifications', 
                    element: (
                        <Suspense fallback={loading()}>
                            <NotificationsPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'suppliers', 
                    element: (
                        <Suspense fallback={loading()}>
                            <SuppliersPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'suppliers/new', 
                    element: (
                        <Suspense fallback={loading()}>
                            <SupplierForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'suppliers/:uuid', 
                    element: (
                        <Suspense fallback={loading()}>
                            <SupplierDetail />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'suppliers/:uuid/edit', 
                    element: (
                        <Suspense fallback={loading()}>
                            <SupplierForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'permissions/new', 
                    element: (
                        <Suspense fallback={loading()}>
                            <PermissionCreatePage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'permissions/:uuid/edit', 
                    element: (
                        <Suspense fallback={loading()}>
                            <PermissionEditPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'my-sales', 
                    element: (
                        <Suspense fallback={loading()}>
                            <MySalesPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'my-sales/:uuid', 
                    element: (
                        <Suspense fallback={loading()}>
                            <MySaleDetailPage />
                        </Suspense>
                    ) 
                },
                {
                    path: 'reports',
                    element: <LoadComponent component={ReportsPage} />,
                },
                {
                    path: 'reports/supplier-profit-loss',
                    element: <LoadComponent component={SupplierSalesReportPage} />,
                },
                { 
                    path: 'user-profile', 
                    element: (
                        <Suspense fallback={loading()}>
                            <AdminProfile />
                        </Suspense>
                    ) 
                },
                // Billing routes (admin POS access)
                { 
                    path: 'billing', 
                    element: (
                        <Suspense fallback={loading()}>
                            <BillingPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'billing/select-customer', 
                    element: (
                        <Suspense fallback={loading()}>
                            <SelectCustomerPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'billing/payment', 
                    element: (
                        <Suspense fallback={loading()}>
                            <PaymentPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'billing/customer-invoice', 
                    element: (
                        <Suspense fallback={loading()}>
                            <CustomerInvoicePage />
                        </Suspense>
                    ) 
                },
                // Menu Billing - Restaurant POS with menu items
                {
                    path: 'menu-billing',
                    element: (
                        <Suspense fallback={loading()}>
                            <MenuBillingPage />
                        </Suspense>
                    )
                },
                // Menu routes (restaurant only)
                { 
                    path: 'menu', 
                    element: (
                        <Suspense fallback={loading()}>
                            <MenuListPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'menu/new', 
                    element: (
                        <Suspense fallback={loading()}>
                            <MenuItemForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'menu/edit/:uuid', 
                    element: (
                        <Suspense fallback={loading()}>
                            <MenuItemForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'menu/categories', 
                    element: (
                        <Suspense fallback={loading()}>
                            <MenuCategoryManagement />
                        </Suspense>
                    ) 
                },
                // Demand routes (restaurant only)
                { 
                    path: 'demands', 
                    element: (
                        <Suspense fallback={loading()}>
                            <DemandListPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'demands/create', 
                    element: (
                        <Suspense fallback={loading()}>
                            <DemandCreatePage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'demands/invoices', 
                    element: (
                        <Suspense fallback={loading()}>
                            <DemandInvoicesPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'demands/:uuid', 
                    element: (
                        <Suspense fallback={loading()}>
                            <DemandDetailPage />
                        </Suspense>
                    ) 
                },
                // Catch-all for unknown admin routes
                { 
                    path: '*', 
                    element: <LoadComponent component={NotFound} /> 
                },
            ],
        },
        {
            // super admin protected routes
            path: '/super-admin',
            element: <SuperAdminRoute />,
            children: [
                { 
                    path: 'dashboard', 
                    element: (
                        <Suspense fallback={loading()}>
                            <SuperAdminDashboard />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'clients', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ClientsListPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'clients/new', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ClientForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'clients/:clientId', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ClientDetailPage />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'clients/:clientId/edit', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ClientForm />
                        </Suspense>
                    ) 
                },
                { 
                    path: 'contact-queries', 
                    element: (
                        <Suspense fallback={loading()}>
                            <ContactQueriesPage />
                        </Suspense>
                    ) 
                },
                // Catch-all for unknown super-admin routes
                { 
                    path: '*', 
                    element: <LoadComponent component={NotFound} /> 
                },
            ],
        },
        {
            // protected routes for admin role
            path: '/',
            element: <PrivateRoute role={'admin'} />,
            children: [
                {
                    path: 'pages',
                    children: [
                        {
                            path: 'account',
                            children: [
                                { path: 'dashboard', element: <LoadComponent component={Dashboard} /> },
                                { path: 'settings', element: <LoadComponent component={Settings} /> },
                            ],
                        },
                    ],
                },
            ],
        },
        // Global catch-all 404 route (must be last)
        { 
            path: '*', 
            element: <LoadComponent component={NotFound} /> 
        },
    ]);
};

export default AllRoutes;
